import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ReportCtx } from './reports/reportCtx';
import axios from 'axios';
import {
  ChevronDown, ChevronRight, Edit3, AlertTriangle, Clock, Send,
  CheckCircle2, XCircle, Loader2, RefreshCw, FileText, Info, CheckSquare, Square, Wand2, Plus, Trash2, Camera,
  Lock, Unlock, CalendarDays,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import PayrollAdjustments from './PayrollAdjustments';
import {
  EDIT_REASONS, isoToLocalInput, localInputToIso, fmtTime, fmtHours,
  scrollWorkerIntoView, Stat, Flag, StatusPill, DetailsTable,
} from './payroll/workbenchShared';
import { KioskPhotoDialog } from './payroll/KioskPhotoDialog';
import { DeleteSessionDialog } from './payroll/DeleteSessionDialog';
import { SessionFixDialog } from './payroll/SessionFixDialog';
import { EditSessionDialog } from './payroll/EditSessionDialog';
import { AddSessionDialog } from './payroll/AddSessionDialog';
import { WeekEntryDialog } from './payroll/WeekEntryDialog';
import { BulkNormalizeDialog } from './payroll/BulkNormalizeDialog';
import { PushToQboDialog } from './payroll/PushToQboDialog';
import { loadPayrollView, savePayrollView } from '../utils/payrollView';

const API = `${window.__BACKEND_URL__}/api`;

const PayrollWorkbench = ({ data, token, startDate, endDate, onRefresh, onPatchRow, serverPaging, mode = 'review' }) => {
  // mode 'review' (Reports → Payroll Report): time entries for the period + editing only.
  // mode 'payroll' (Money → Payroll → Process & Push): adds lock/checklist/Push to QuickBooks.
  const isPayrollMode = mode === 'payroll';
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_super_user === true;
  const rows = data?.rows || [];
  const paged = !!data?.paged && !!serverPaging;
  // Bumped after every in-place row patch so PayrollAdjustments reloads.
  const [patchTick, setPatchTick] = useState(0);
  // Single-worker refresh (Sep 2026): recompute only the edited worker's
  // row and swap it in place — no table reload, no scroll jump, expanded
  // rows stay open. Falls back to a full refresh if the parent can't patch.
  const toggleCallout = async (workerId, s) => {
    const next = !s.callback_shift;
    const reason = window.prompt(next ? 'Reason for tagging this session as an after-hours call-out:' : 'Reason for removing the call-out tag:', next ? 'Worker was called out after hours' : '');
    if (reason === null) return;
    try {
      await axios.patch(`${API}/time-entries/${s.entry_id}/callout`, { callback_shift: next, reason }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshWorker(workerId);
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Could not update the call-out tag');
    }
  };
  const refreshWorker = async (workerId) => {
    if (!workerId || !onPatchRow) { await onRefresh?.(); return; }
    try {
      const res = await axios.get(`${API}/reports/v2/payroll`, {
        params: { start_date: startDate, end_date: endDate, worker_id: workerId },
        headers: { Authorization: `Bearer ${token}` },
      });
      onPatchRow(workerId, res.data?.rows?.[0] || null);
      setPatchTick((t) => t + 1);
    } catch {
      await onRefresh?.();
    }
  };
  // When set, keep this worker's row pinned in view across ALL re-renders
  // triggered by a save + refresh — including the LATE transient layout
  // collapse (~1-2s after the data lands) that clamps page scroll back to
  // the top. The pin lasts 6s and cancels the moment the admin scrolls or
  // clicks somewhere else on purpose.
  const pendingScrollWorkerRef = useRef(null);
  useEffect(() => {
    if (!pendingScrollWorkerRef.current) return undefined;
    const wid = pendingScrollWorkerRef.current;
    const until = performance.now() + 6000;
    let raf;
    const cancel = () => {
      if (pendingScrollWorkerRef.current === wid) pendingScrollWorkerRef.current = null;
    };
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchmove', cancel, { passive: true });
    window.addEventListener('pointerdown', cancel, { passive: true });
    const fullyOutOfView = () => {
      const el = document.querySelector(`[data-testid="pw-worker-row-${wid}"]`);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      return r.bottom < 0 || r.top > vh;
    };
    const tick = () => {
      if (pendingScrollWorkerRef.current !== wid || performance.now() > until) {
        if (pendingScrollWorkerRef.current === wid) pendingScrollWorkerRef.current = null;
        return;
      }
      if (fullyOutOfView()) scrollWorkerIntoView(wid);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchmove', cancel);
      window.removeEventListener('pointerdown', cancel);
    };
  }, [data]);
  const [expanded, setExpanded] = useState(() => {
    if (!serverPaging?.remember) return {};
    const saved = loadPayrollView(user?.id).expanded;
    return saved && typeof saved === 'object' ? saved : {};
  });
  useEffect(() => {
    if (serverPaging?.remember) savePayrollView(user?.id, { expanded });
  }, [expanded, serverPaging?.remember, user?.id]);
  const resetView = () => {
    setExpanded({});
    setRowSearch('');
    setRowPage(1);
    serverPaging?.onReset?.();
  };
  const [editing, setEditing] = useState(null); // { session, worker }
  const [editForm, setEditForm] = useState({
    new_clock_in: '',
    new_clock_out: '',
    reason_kind: '',
    notes: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  // Project + Job catalogues for the "fix wrong assignment" Selects on
  // the Edit Session dialog. Lazy-loaded on first dialog open; cached
  // for the life of the component.
  const [projects, setProjects] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(false);
  const [jobsCatalog, setJobsCatalog] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);

  // Load the project catalogue. A failed/empty load is NOT cached — the
  // next dialog open (or Retry) fetches again, so a single 401/network
  // blip can no longer leave admins with a permanently empty dropdown.
  const loadProjects = () => {
    if (projectsLoading) return;
    setProjectsLoading(true);
    setProjectsError(false);
    axios
      .get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : r.data?.projects || []))
      .catch(() => { setProjects(null); setProjectsError(true); })
      .finally(() => setProjectsLoading(false));
  };
  const ensureProjects = () => {
    if (projects === null || projects.length === 0) loadProjects();
  };
  const recentProjectIds = (worker) =>
    [...new Set((worker?.sessions || []).map((s) => s.project_id).filter(Boolean))];

  // Admin "Add session" dialog (Feb 2026) — for workers who forgot to
  // clock in for a day. Same project/job/reason fields as the Edit
  // dialog, plus a `clock_in` / `clock_out` pair built from a date +
  // start time + hours so admins can type "8 hours on 2026-06-28
  // starting at 7am" without juggling two datetime-local inputs.
  const [addingForWorker, setAddingForWorker] = useState(null);
  // Week Entry Mode (Oct 2026) — one grid for a worker's whole week.
  const [weekFor, setWeekFor] = useState(null);
  // Deep link from Missed Clock-In alerts: ?worker=<id>&add=1 expands the row and opens Add session.
  const deepLinkRef = useRef(null);
  if (deepLinkRef.current === null) {
    const p = new URLSearchParams(window.location.search);
    deepLinkRef.current = p.get('worker') ? { worker: p.get('worker'), add: p.get('add') === '1', q: p.get('q') || '' } : false;
  }
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl || !rows.length) return;
    const w = rows.find((r) => r.worker_id === dl.worker);
    if (!w) return;
    deepLinkRef.current = false;
    setExpanded((p) => ({ ...p, [w.worker_id]: true }));
    pendingScrollWorkerRef.current = w.worker_id;
    if (dl.add) openAdd(w);
  }, [rows]);
  const [addForm, setAddForm] = useState({
    date: '',
    start_time: '08:00',
    hours: '8',
    project_id: '__none__',
    job_id: '__none__',
    reason_kind: 'manual_adjustment',
    notes: '',
  });
  const [savingAdd, setSavingAdd] = useState(false);

  // Bulk-normalize state (Feb 2026) — admin selects N workers, picks a
  // target (each worker's shift duration / fixed hours / specific day),
  // confirms an audit reason, and we POST /time-entries/bulk-edit once
  // for every selected entry. Replaces the "click into Time Editor for
  // each worker" tedium when running payroll for 10–50 people.
  const [selectedWorkers, setSelectedWorkers] = useState({}); // { worker_id: true }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    mode: 'apply_shift_duration', // | 'fixed_hours' | 'custom'
    fixed_hours: '8',
    day_filter: '',               // YYYY-MM-DD; empty = entire period
    reason_kind: 'manual_adjustment',
    notes: '',
  });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null); // { entries, workers, hoursLabel }

  // QBO push state
  const [pushOpen, setPushOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [excludedIds, setExcludedIds] = useState([]);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [rowSearch, setRowSearch] = useState(serverPaging?.query || (deepLinkRef.current && deepLinkRef.current.q) || '');
  // Legacy / no-project sessions (Sep 2026) — attribution only, pay is unaffected
  const [legacyOnly, setLegacyOnly] = useState(false);
  const [hideExempt, setHideExempt] = useState(false);
  const { onDrill: pwDrill } = useContext(ReportCtx);
  const isLegacySession = (s) => !s.project_name || s.project_number === 'GENERAL';
  const legacyHours = (r) => (r.sessions || []).filter(isLegacySession).reduce((a, s) => a + (Number(s.hours) || 0), 0);
  const legacyTotals = rows.reduce((acc, r) => { const h = legacyHours(r); if (h > 0) { acc.workers += 1; acc.hours += h; } return acc; }, { workers: 0, hours: 0 });
  const [rowPage, setRowPage] = useState(1);
  const PW_PAGE_SIZE = 50;
  // Server paging: debounce the search box into the parent's query param.
  useEffect(() => {
    if (!serverPaging) return undefined;
    const t = setTimeout(() => { if (rowSearch !== serverPaging.query) { lastPushedQuery.current = rowSearch; serverPaging.onQueryChange(rowSearch); } }, 350);
    return () => clearTimeout(t);
  }, [rowSearch, serverPaging]);
  // Keep the box in sync when the query is set from outside (e.g. "Payroll (this worker)" jump from a drill-down).
  const lastPushedQuery = useRef(serverPaging?.query || '');
  useEffect(() => {
    if (!serverPaging || serverPaging.query === undefined) return;
    if (serverPaging.query !== lastPushedQuery.current) { lastPushedQuery.current = serverPaging.query; setRowSearch(serverPaging.query || ''); }
  }, [serverPaging?.query]); // eslint-disable-line
  const searchedRows = !paged && rowSearch.trim()
    ? rows.filter((r) => (r.worker_name || '').toLowerCase().includes(rowSearch.trim().toLowerCase()))
    : rows;
  const filteredRows = searchedRows.filter((r) => (!legacyOnly || legacyHours(r) > 0) && !(hideExempt && r.qb_payroll_exempt));
  const rowPageCount = paged ? (data.page_count || 1) : Math.max(1, Math.ceil(filteredRows.length / PW_PAGE_SIZE));
  const safeRowPage = paged ? (data.page || 1) : Math.min(rowPage, rowPageCount);
  const goPage = (p) => (paged ? serverPaging.onPageChange(p) : setRowPage(p));
  const pagedRows = paged ? rows.filter((r) => (!legacyOnly || legacyHours(r) > 0) && !(hideExempt && r.qb_payroll_exempt)) : filteredRows.slice((safeRowPage - 1) * PW_PAGE_SIZE, safeRowPage * PW_PAGE_SIZE);
  const exemptCount = rows.filter((r) => r.qb_payroll_exempt).length;
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushProgress, setPushProgress] = useState(null);

  const selectedWorkerIds = useMemo(
    () => Object.keys(selectedWorkers).filter((k) => selectedWorkers[k]),
    [selectedWorkers],
  );
  const allSelected = rows.length > 0 && selectedWorkerIds.length === rows.length;
  const someSelected = selectedWorkerIds.length > 0 && !allSelected;

  const toggleWorker = (wid) =>
    setSelectedWorkers((p) => ({ ...p, [wid]: !p[wid] }));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedWorkers({});
    } else {
      const all = {};
      rows.forEach((r) => { all[r.worker_id] = true; });
      setSelectedWorkers(all);
    }
  };
  const clearSelection = () => setSelectedWorkers({});

  const stats = useMemo(() => {
    if (paged && data?.summary) {
      const s = data.summary;
      return {
        workers: s.workers, totalHours: Number(s.total_hours || 0).toFixed(2), totalOT: Number(s.overtime_hours || 0).toFixed(2),
        totalAnomalies: s.anomalies || 0, totalGross: s.gross_pay || 0, hasRates: !!s.has_rates,
      };
    }
    const totalHours = rows.reduce((a, r) => a + (r.total_hours || 0), 0);
    const totalOT = rows.reduce((a, r) => a + (r.overtime_hours || 0), 0);
    const totalAnomalies = rows.reduce((a, r) => a + (r.anomalies?.length || 0), 0);
    const totalGross = rows.reduce((a, r) => a + (r.gross_pay || 0), 0);
    const hasRates = rows.some((r) => r.hourly_rate != null);
    return {
      workers: rows.length,
      totalHours: totalHours.toFixed(2),
      totalOT: totalOT.toFixed(2),
      totalAnomalies,
      totalGross,
      hasRates,
    };
  }, [rows, paged, data]);

  const fmtDollar = (v) =>
    v != null ? `$${Number(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  const toggle = (wid) => setExpanded((p) => ({ ...p, [wid]: !p[wid] }));

  // Admin-only: delete an erroneous time entry (e.g. clocked in on a day off)
  const [deleting, setDeleting] = useState(null); // { worker, session }
  const [deleteReason, setDeleteReason] = useState('');
  // Kiosk punch photo viewer — {worker, session, photos: [{id, label}]}
  const [photoView, setPhotoView] = useState(null);
  const [photoData, setPhotoData] = useState({}); // photo_id -> {photo_b64, at} | 'loading' | 'error'

  const openPunchPhotos = (w, s) => {
    const photos = [];
    if (s.punch_photo_id) photos.push({ id: s.punch_photo_id, label: 'Clock-in' });
    if (s.punch_out_photo_id) photos.push({ id: s.punch_out_photo_id, label: 'Clock-out' });
    setPhotoView({ worker: w, session: s, photos });
    photos.forEach(async (p) => {
      setPhotoData((d) => ({ ...d, [p.id]: d[p.id] && d[p.id] !== 'error' ? d[p.id] : 'loading' }));
      try {
        const r = await axios.get(`${API}/kiosk-punch-photos/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setPhotoData((d) => ({ ...d, [p.id]: r.data }));
      } catch {
        setPhotoData((d) => ({ ...d, [p.id]: 'error' }));
      }
    });
  };
  const [deletingBusy, setDeletingBusy] = useState(false);

  const confirmDelete = async () => {
    if (!deleting) return;
    if (!deleteReason.trim() || deleteReason.trim().length < 3) {
      toast({ title: 'Reason required', description: 'Add a short reason so the deletion is auditable.', variant: 'destructive' });
      return;
    }
    setDeletingBusy(true);
    try {
      await axios.delete(`${API}/time-entries/${deleting.session.entry_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { deletion_reason: deleteReason.trim() },
      });
      toast({ title: 'Time entry deleted', description: `${deleting.worker.worker_name} — ${deleting.session.date}` });
      const wid = deleting.worker.worker_id;
      pendingScrollWorkerRef.current = wid;
      setDeleting(null);
      setDeleteReason('');
      await refreshWorker(wid);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Could not delete the time entry.', variant: 'destructive' });
    } finally {
      setDeletingBusy(false);
    }
  };

  const openEdit = (worker, session) => {
    setEditing({ worker, session });
    setEditForm({
      new_clock_in: isoToLocalInput(session.clock_in),
      new_clock_out: isoToLocalInput(session.clock_out),
      // Re-assignment fields default to "no change" (undefined). The
      // dropdown will pre-select the current project_id so unchanged
      // saves don't touch the field.
      new_project_id: session.project_id || '__none__',
      new_job_id: session.job_id || '__none__',
      // Default to a sensible reason so the Save button is enabled from
      // the start. Admins can still change it. Resolves the long-standing
      // "I clicked Save but nothing happened" UX trap where the disabled
      // state wasn't obvious.
      reason_kind: 'manual_adjustment',
      notes: '',
    });
    // Lazy-load the project catalogue (retries if a prior load failed).
    ensureProjects();
    // Lazy-load the jobs catalogue once.
    if (jobsCatalog === null && !jobsLoading) {
      setJobsLoading(true);
      axios
        .get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : r.data?.jobs || [];
          // Sort: active jobs first (in_progress > pending > paused),
          // completed last. Within each, newest first.
          const statusRank = { in_progress: 0, pending: 1, paused: 2, completed: 3, invoiced: 4 };
          list.sort((a, b) => {
            const ra = statusRank[a.status] ?? 99;
            const rb = statusRank[b.status] ?? 99;
            if (ra !== rb) return ra - rb;
            return (b.updated_at || '').localeCompare(a.updated_at || '');
          });
          setJobsCatalog(list);
        })
        .catch(() => setJobsCatalog([]))
        .finally(() => setJobsLoading(false));
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.reason_kind) {
      toast({ title: 'Reason required', description: 'Pick a reason class so the edit is auditable.', variant: 'destructive' });
      return;
    }
    const reasonLabel = EDIT_REASONS.find((r) => r.value === editForm.reason_kind)?.label || editForm.reason_kind;
    const reason = editForm.notes
      ? `[${editForm.reason_kind}] ${reasonLabel} — ${editForm.notes}`
      : `[${editForm.reason_kind}] ${reasonLabel}`;
    const payload = {
      reason,
    };
    const newIn = localInputToIso(editForm.new_clock_in);
    const newOut = localInputToIso(editForm.new_clock_out);
    if (newIn && newIn !== editing.session.clock_in) payload.new_clock_in = newIn;
    if (newOut && newOut !== editing.session.clock_out) payload.new_clock_out = newOut;
    // Project re-assignment — send when admin picked something different
    // from the current value. '__none__' means "remove project tag".
    const currentProjectId = editing.session.project_id || '__none__';
    if (editForm.new_project_id !== undefined && editForm.new_project_id !== currentProjectId) {
      if (editForm.new_project_id === '__none__') {
        payload.new_project_id = '';
      } else {
        payload.new_project_id = editForm.new_project_id;
        const proj = (projects || []).find((p) => p.id === editForm.new_project_id);
        if (proj) {
          payload.new_project_name = proj.name || '';
          payload.new_project_number = proj.project_number || '';
        }
      }
    }
    // Job re-assignment — same shape. '__none__' clears the job tag.
    const currentJobId = editing.session.job_id || '__none__';
    if (editForm.new_job_id !== undefined && editForm.new_job_id !== currentJobId) {
      payload.new_job_id = editForm.new_job_id === '__none__' ? '' : editForm.new_job_id;
    }
    if (
      !payload.new_clock_in &&
      !payload.new_clock_out &&
      payload.new_project_id === undefined &&
      payload.new_job_id === undefined
    ) {
      toast({ title: 'No changes', description: 'Adjust at least one field.', variant: 'destructive' });
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/time-entries/${editing.session.entry_id}/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Time entry updated', description: `${editing.worker.worker_name} — ${editing.session.date}` });
      // Keep the admin exactly where they were: close the modal without
      // Radix stealing focus (handled on DialogContent), preserve the
      // expanded worker, and restore scroll after the data refresh.
      const editedWorkerId = editing.worker.worker_id;
      pendingScrollWorkerRef.current = editedWorkerId;
      setEditing(null);
      await refreshWorker(editedWorkerId);
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e.response?.data?.detail || 'Could not update the time entry.',
        variant: 'destructive',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  // Per-session batch fix (Jun 2026) — checkboxes on individual time
  // entries inside a worker's expanded row so admins can normalize a
  // handful of sessions (e.g. rounding errors → exactly 8h, or snap a
  // set-shift worker to 8:00–16:00) in one save before payroll.
  const [sessSel, setSessSel] = useState({}); // { entry_id: true }
  const [sessFix, setSessFix] = useState(null); // { worker, entryIds }
  const [sessForm, setSessForm] = useState({
    mode: 'hours', hours: '8', start_time: '08:00', end_time: '16:00',
    reason_kind: 'manual_adjustment', notes: '',
  });
  const [sessSaving, setSessSaving] = useState(false);

  // Payroll period lock (Jun 2026) — when the visible range overlaps a
  // locked period, editing is disabled and a banner explains who locked it.
  const [locks, setLocks] = useState([]);
  const [lockBusy, setLockBusy] = useState(false);
  const loadLocks = async () => {
    try {
      const res = await axios.get(`${API}/payroll/locks`, { headers: { Authorization: `Bearer ${token}` } });
      setLocks(res.data.locks || []);
    } catch { /* keep last known state */ }
  };
  useEffect(() => { loadLocks(); /* eslint-disable-line */ }, [token]);
  const activeLock = useMemo(
    () => locks.find((l) => l.start_date <= endDate && l.end_date >= startDate) || null,
    [locks, startDate, endDate]
  );
  const locked = !!activeLock;

  const lockPeriod = async () => {
    if (!window.confirm(`Lock ${startDate} → ${endDate}? Nobody will be able to edit, add or delete time entries in this period until it's unlocked.`)) return;
    setLockBusy(true);
    try {
      await axios.post(`${API}/payroll/locks`, { start_date: startDate, end_date: endDate }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Pay period locked', description: `${startDate} → ${endDate} is now read-only.` });
      await loadLocks();
    } catch (e) {
      toast({ title: 'Could not lock', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setLockBusy(false); }
  };
  const unlockPeriod = async () => {
    if (!activeLock) return;
    const reason = window.prompt(`Unlock ${activeLock.start_date} → ${activeLock.end_date}?\n\nLocked entries go back to payroll-approved and time edits are accepted again (already-pushed/paid days stay put). Enter a short reason for the audit log:`);
    if (reason === null) return;
    if (reason.trim().length < 5) { toast({ title: 'Reason required', description: 'Give at least 5 characters so the unlock is traceable.', variant: 'destructive' }); return; }
    setLockBusy(true);
    try {
      const { data } = await axios.post(`${API}/time/unlock-pay-period`, { start_date: activeLock.start_date, end_date: activeLock.end_date, reason: reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Pay period unlocked', description: `${data.unlocked} entr${data.unlocked === 1 ? 'y' : 'ies'} back to payroll-approved · ${data.period_locks_removed} period lock${data.period_locks_removed === 1 ? '' : 's'} removed.` });
      await loadLocks();
      onRefresh?.();
    } catch (e) {
      toast({ title: 'Could not unlock', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setLockBusy(false); }
  };

  // Pre-payroll checklist (Jun 2026 — Phase 5)
  const [checklist, setChecklist] = useState(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistBusy, setChecklistBusy] = useState(false);

  // Averaging-agreement audit + OT approach alerts (Aug 2026)
  const [missingAgreements, setMissingAgreements] = useState(null); // {count, workers}
  const [missingOpen, setMissingOpen] = useState(false);
  const [otAlertCfg, setOtAlertCfg] = useState(null);
  const [otAlertOpen, setOtAlertOpen] = useState(false);
  const [otAlertSaving, setOtAlertSaving] = useState(false);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/payroll/missing-agreements`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMissingAgreements(r.data))
      .catch(() => {});
    if (isAdmin) {
      axios.get(`${API}/payroll/ot-alert-settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setOtAlertCfg(r.data))
        .catch(() => {});
    }
  }, [token, isAdmin]);
  const saveOtAlertCfg = async () => {
    setOtAlertSaving(true);
    try {
      const { data: saved } = await axios.put(`${API}/payroll/ot-alert-settings`, otAlertCfg, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOtAlertCfg(saved);
      setOtAlertOpen(false);
      toast({
        title: saved.enabled ? 'OT alerts enabled' : 'OT alerts disabled',
        description: saved.enabled
          ? `Admins are emailed when an averaging worker is within ${saved.hours_before}h of their period cap.`
          : 'No overtime-approach emails will be sent.',
      });
    } catch (e) {
      toast({ title: 'Could not save OT alert settings', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setOtAlertSaving(false); }
  };

  const runChecklist = async () => {
    setChecklistBusy(true);
    try {
      const { data } = await axios.get(`${API}/payroll/pre-lock-check`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
      });
      setChecklist(data);
      setChecklistOpen(true);
    } catch (e) {
      toast({ title: 'Checklist failed', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setChecklistBusy(false); }
  };

  // Rounding flag (Jun 2026): completed sessions within ±15 min of a whole
  // number of hours — likely punch rounding worth normalizing before payroll.
  const roundingFlag = (s) => {
    if (!s.clock_out || !s.hours) return null;
    const nearest = Math.round(s.hours);
    if (nearest < 1 || nearest > 16) return null;
    const diff = Math.abs(s.hours - nearest);
    return diff > 0.001 && diff <= 0.25 ? nearest : null;
  };

  const toggleSession = (eid) => setSessSel((p) => ({ ...p, [eid]: !p[eid] }));
  const workerSelectedIds = (w) =>
    (w.sessions || []).filter((s) => s.entry_id && sessSel[s.entry_id]).map((s) => s.entry_id);
  const toggleAllSessions = (w) => {
    const ids = (w.sessions || []).filter((s) => s.entry_id).map((s) => s.entry_id);
    const all = ids.length > 0 && ids.every((id) => sessSel[id]);
    setSessSel((p) => {
      const n = { ...p };
      ids.forEach((id) => { n[id] = !all; });
      return n;
    });
  };
  const openSessFix = (w) => {
    setSessForm({ mode: 'hours', hours: '8', start_time: '08:00', end_time: '16:00', reason_kind: 'manual_adjustment', notes: '' });
    setSessFix({ worker: w, entryIds: workerSelectedIds(w) });
  };

  const saveSessFix = async () => {
    if (!sessFix) return;
    const { worker, entryIds } = sessFix;
    if (sessForm.mode === 'hours') {
      const h = parseFloat(sessForm.hours);
      if (!h || h < 0.25 || h > 24) {
        toast({ title: 'Hours must be between 0.25 and 24', variant: 'destructive' });
        return;
      }
    }
    const reasonLabel = EDIT_REASONS.find((r) => r.value === sessForm.reason_kind)?.label || sessForm.reason_kind;
    const reason = `[sessions-${sessForm.mode === 'hours' ? 'fixed_hours' : 'exact_times'}] ${reasonLabel}${sessForm.notes ? ` — ${sessForm.notes}` : ''}`;
    const payload = { entry_ids: entryIds, reason };
    if (sessForm.mode === 'hours') {
      payload.apply_hours = parseFloat(sessForm.hours);
    } else {
      payload.set_start_time = sessForm.start_time;
      payload.set_end_time = sessForm.end_time;
    }
    setSessSaving(true);
    try {
      const { data } = await axios.post(`${API}/time-entries/bulk-edit`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Sessions updated', description: `${data.edited_count} session(s) fixed for ${worker.worker_name}` });
      pendingScrollWorkerRef.current = worker.worker_id;
      setSessFix(null);
      setSessSel((p) => {
        const n = { ...p };
        entryIds.forEach((id) => delete n[id]);
        return n;
      });
      await refreshWorker(worker.worker_id);
    } catch (e) {
      toast({ title: 'Batch fix failed', description: e.response?.data?.detail || 'Could not update the sessions.', variant: 'destructive' });
    } finally {
      setSessSaving(false);
    }
  };

  // Open the Add-Session dialog for a specific worker. Lazy-loads
  // project + job catalogues just like Edit so the Selects are ready.
  const openAdd = (worker) => {
    // Default the date to today (admin's local tz) and start at 8 AM.
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const ymd = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    setAddingForWorker(worker);
    setAddForm({
      date: ymd,
      start_time: '08:00',
      hours: '8',
      project_id: '__none__',
      job_id: '__none__',
      reason_kind: 'manual_adjustment',
      notes: '',
    });
    // Reuse the same lazy-load logic.
    ensureProjects();
    if (jobsCatalog === null && !jobsLoading) {
      setJobsLoading(true);
      axios
        .get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : r.data?.jobs || [];
          const statusRank = { in_progress: 0, pending: 1, paused: 2, completed: 3, invoiced: 4 };
          list.sort((a, b) => {
            const ra = statusRank[a.status] ?? 99;
            const rb = statusRank[b.status] ?? 99;
            if (ra !== rb) return ra - rb;
            return (b.updated_at || '').localeCompare(a.updated_at || '');
          });
          setJobsCatalog(list);
        })
        .catch(() => setJobsCatalog([]))
        .finally(() => setJobsLoading(false));
    }
  };

  const submitAdd = async () => {
    if (!addingForWorker) return;
    // Build clock_in / clock_out from date + start_time + hours.
    const hours = parseFloat(addForm.hours);
    if (Number.isNaN(hours) || hours < 0.25 || hours > 24) {
      toast({ title: 'Invalid hours', description: 'Hours must be between 0.25 and 24.', variant: 'destructive' });
      return;
    }
    if (!addForm.date || !addForm.start_time) {
      toast({ title: 'Missing date/time', description: 'Pick a date and a start time.', variant: 'destructive' });
      return;
    }
    if (!addForm.reason_kind) {
      toast({ title: 'Reason required', description: 'Pick a reason class for the audit log.', variant: 'destructive' });
      return;
    }
    // Treat the date + time as local wall-clock; convert to ISO UTC.
    const local = new Date(`${addForm.date}T${addForm.start_time}`);
    if (Number.isNaN(local.getTime())) {
      toast({ title: 'Invalid date', description: 'Could not parse the date/time.', variant: 'destructive' });
      return;
    }
    const clockIn = local.toISOString();
    const clockOut = new Date(local.getTime() + hours * 3600 * 1000).toISOString();
    const reasonLabel = EDIT_REASONS.find((r) => r.value === addForm.reason_kind)?.label || addForm.reason_kind;
    const reason = addForm.notes
      ? `[${addForm.reason_kind}] ${reasonLabel} — ${addForm.notes}`
      : `[${addForm.reason_kind}] ${reasonLabel}`;
    const payload = {
      user_id: addingForWorker.worker_id,
      clock_in: clockIn,
      clock_out: clockOut,
      reason,
    };
    if (addForm.project_id && addForm.project_id !== '__none__') {
      payload.project_id = addForm.project_id;
      const proj = (projects || []).find((p) => p.id === addForm.project_id);
      if (proj) {
        payload.project_name = proj.name || '';
        payload.project_number = proj.project_number || '';
      }
    }
    if (addForm.job_id && addForm.job_id !== '__none__') {
      payload.job_id = addForm.job_id;
    }
    setSavingAdd(true);
    try {
      const res = await axios.post(
        `${API}/time-entries/admin-create`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: `${res.data.hours}h added for ${res.data.worker_name}`,
        description:
          addForm.date < startDate || addForm.date > endDate
            ? `Session on ${addForm.date} from ${addForm.start_time}. Outside current range (${startDate} → ${endDate}) — switch the date range to see it here.`
            : `Session on ${addForm.date} from ${addForm.start_time}.`,
      });
      const addWorkerId = addingForWorker?.worker_id;
      if (addWorkerId) pendingScrollWorkerRef.current = addWorkerId;
      setAddingForWorker(null);
      await refreshWorker(addWorkerId);
    } catch (e) {
      toast({
        title: 'Add failed',
        description: e.response?.data?.detail || 'Could not add the session.',
        variant: 'destructive',
      });
    } finally {
      setSavingAdd(false);
    }
  };

  // Compute which entry_ids the current bulk selection + day filter
  // would target, plus a human-readable preview for the confirm modal.
  const computeBulkTargets = (form) => {
    const dayFilter = form.day_filter?.trim();
    const targetWorkers = rows.filter((r) => selectedWorkers[r.worker_id]);
    const entries = [];
    for (const w of targetWorkers) {
      for (const s of w.sessions || []) {
        if (!s.entry_id) continue;
        if (dayFilter && s.date !== dayFilter) continue;
        entries.push({ entry_id: s.entry_id, worker_name: w.worker_name, date: s.date, hours: s.hours });
      }
    }
    return { entries, workers: targetWorkers };
  };

  const openBulk = () => {
    if (selectedWorkerIds.length === 0) return;
    setBulkOpen(true);
    setBulkPreview(computeBulkTargets(bulkForm));
  };

  // Recompute preview whenever the form's filters change while open
  const updateBulkForm = (patch) => {
    setBulkForm((f) => {
      const next = { ...f, ...patch };
      setBulkPreview(computeBulkTargets(next));
      return next;
    });
  };

  const submitBulk = async () => {
    if (!bulkPreview || bulkPreview.entries.length === 0) {
      toast({ title: 'Nothing to update', description: 'No sessions match the selection.', variant: 'destructive' });
      return;
    }
    const reasonLabel = EDIT_REASONS.find((r) => r.value === bulkForm.reason_kind)?.label || bulkForm.reason_kind;
    const reasonPrefix = bulkForm.day_filter
      ? `bulk-${bulkForm.mode}@${bulkForm.day_filter}`
      : `bulk-${bulkForm.mode}@period`;
    const reason = bulkForm.notes
      ? `[${reasonPrefix}] ${reasonLabel} — ${bulkForm.notes}`
      : `[${reasonPrefix}] ${reasonLabel}`;
    const payload = {
      entry_ids: bulkPreview.entries.map((e) => e.entry_id),
      reason,
    };
    if (bulkForm.mode === 'apply_shift_duration') {
      payload.apply_shift_duration = true;
    } else if (bulkForm.mode === 'fixed_hours') {
      const h = parseFloat(bulkForm.fixed_hours);
      if (Number.isNaN(h) || h < 0.25 || h > 24) {
        toast({ title: 'Invalid hours', description: 'Hours must be between 0.25 and 24.', variant: 'destructive' });
        return;
      }
      payload.apply_hours = h;
    }
    setBulkSaving(true);
    try {
      const res = await axios.post(
        `${API}/time-entries/bulk-edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const edited = res.data?.edited_count ?? bulkPreview.entries.length;
      toast({
        title: `${edited} session${edited === 1 ? '' : 's'} normalized`,
        description: `${bulkPreview.workers.length} worker${bulkPreview.workers.length === 1 ? '' : 's'} updated.`,
      });
      const firstBulkWorkerId = selectedWorkerIds[0];
      if (firstBulkWorkerId) pendingScrollWorkerRef.current = firstBulkWorkerId;
      setBulkOpen(false);
      clearSelection();
      { await onRefresh?.(); if (firstBulkWorkerId) scrollWorkerIntoView(firstBulkWorkerId); }
    } catch (e) {
      toast({
        title: 'Bulk normalize failed',
        description: e.response?.data?.detail || 'Could not apply the bulk edit.',
        variant: 'destructive',
      });
    } finally {
      setBulkSaving(false);
    }
  };

  // Build a list of distinct YYYY-MM-DD dates present across selected
  // workers' sessions — drives the optional day-filter Select so admins
  // can normalize one specific day instead of the whole period.
  const selectionDays = useMemo(() => {
    const set = new Set();
    for (const w of rows) {
      if (!selectedWorkers[w.worker_id]) continue;
      for (const s of w.sessions || []) {
        if (s.date) set.add(s.date);
      }
    }
    return Array.from(set).sort();
  }, [rows, selectedWorkers]);

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewResult(null);
    setPreviewError(null);
    setPushResult(null);
    try {
      const res = await axios.post(
        `${API}/qb/sync-timesheets/preview`,
        { start_date: startDate, end_date: endDate },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success === false) {
        // Soft failure returned as HTTP 200 (e.g. "QuickBooks not connected")
        // must never render as an empty successful preview on a payroll screen.
        const msg = res.data?.error || 'Could not generate a QuickBooks preview.';
        setPreviewError(msg);
        setPreviewResult(null);
        return;
      }
      setPreviewResult(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not generate a QuickBooks preview.';
      setPreviewError(detail);
      toast({
        title: 'Preview failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const finishPush = (data) => {
    setPushResult(data);
    const errs = data?.errors || 0;
    const pushed = data?.entries_pushed || 0;
    const dupes = data?.already_pushed || 0;
    if (errs === 0) {
      toast({
        title: 'Pushed to QuickBooks',
        description: `${pushed} entries pushed successfully.${dupes > 0 ? ` ${dupes} already-pushed day${dupes === 1 ? '' : 's'} skipped (duplicate protection).` : ''}`,
      });
    } else {
      toast({
        title: `${pushed} pushed, ${errs} failed`,
        description: 'Review the per-worker details below and fix the failing rows.',
        variant: 'destructive',
      });
    }
  };

  // Poll a background push until it finishes — long pushes no longer die at the proxy timeout.
  const waitForPush = async (pushId) => {
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      const st = await axios.get(`${API}/qb/sync-timesheets/status/${pushId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (st.data.status === 'running') { setPushProgress(st.data.days_done || 0); continue; }
      if (st.data.status === 'failed') throw new Error(st.data.error || 'QuickBooks push failed.');
      return st.data.result;
    }
  };

  const runPush = async () => {
    setPushing(true);
    setPushProgress(null);
    try {
      let acknowledgeOverlap = false;
      let started = null;
      while (!started) {
        try {
          started = (await axios.post(
            `${API}/qb/sync-timesheets`,
            { start_date: startDate, end_date: endDate, acknowledge_overlap: acknowledgeOverlap, async: true, exclude_user_ids: excludedIds },
            { headers: { Authorization: `Bearer ${token}` } },
          )).data;
        } catch (e) {
          const detail = e.response?.data?.detail;
          if (e.response?.status === 409 && detail?.code === 'overlap_warning') {
            if (!window.confirm(`${detail.message}\n\nContinue with the push?`)) return;
            acknowledgeOverlap = true;
          } else if (e.response?.status === 409 && detail?.code === 'push_in_progress') {
            toast({ title: 'Push already running', description: detail.message });
            started = { push_id: detail.push_id };
          } else {
            throw e;
          }
        }
      }
      if (started.success === false) {
        const msg = started.error || 'QuickBooks push failed.';
        setPreviewError(msg);
        toast({ title: 'Push failed', description: msg, variant: 'destructive' });
        return;
      }
      const result = started.push_id ? await waitForPush(started.push_id) : started;
      if (result?.success === false) {
        setPreviewError(result.error || 'QuickBooks push failed.');
        toast({ title: 'Push failed', description: result.error || 'QuickBooks push failed.', variant: 'destructive' });
        return;
      }
      finishPush(result);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast({
        title: 'Push failed',
        description: typeof detail === 'string' ? detail : (e.message && !e.response ? e.message : 'QuickBooks push could not complete.'),
        variant: 'destructive',
      });
    } finally {
      setPushing(false);
      setPushProgress(null);
    }
  };

  const downloadPayrollCsv = async () => {
    setCsvDownloading(true);
    try {
      const res = await axios.get(`${API}/qb/payroll-run-csv`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_run_${startDate}_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'CSV backup saved', description: 'Keep it as your fallback if the QuickBooks push fails.' });
    } catch {
      toast({ title: 'CSV download failed', description: 'Try again.', variant: 'destructive' });
    } finally {
      setCsvDownloading(false);
    }
  };

  const downloadEditsCsv = async () => {
    try {
      const res = await axios.get(`${API}/time-entries/edits-csv`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time_adjustments_${startDate}_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Adjustments CSV saved', description: 'Every documented time edit this period — who, when, and the reason.' });
    } catch {
      toast({ title: 'CSV download failed', description: 'Try again.', variant: 'destructive' });
    }
  };

  const closePushDialog = () => {
    setPushOpen(false);
    setPreviewResult(null);
    setPreviewError(null);
    setPushResult(null);
    setExcludedIds([]);
  };

  const totalReady = previewResult?.details?.filter((d) => d.status === 'ready' && !excludedIds.includes(d.worker_id)).length || 0;
  const totalBlockers = previewResult?.details?.filter((d) => d.status === 'error' && !excludedIds.includes(d.worker_id)).length || 0;
  const totalUnapproved = previewResult?.details?.filter((d) => d.status === 'unapproved').length || 0;
  const totalSkipped = previewResult?.details?.filter((d) => d.status === 'skipped').length || 0;
  const totalAlreadyPushed = previewResult?.details?.filter((d) => d.status === 'already_pushed').length || 0;
  const totalExempt = (previewResult?.details?.filter((d) => d.status === 'exempt').length || 0)
    + (previewResult?.details?.filter((d) => ['ready', 'error'].includes(d.status) && excludedIds.includes(d.worker_id)).length || 0);
  const toggleExcluded = (workerId) => setExcludedIds((ids) => (ids.includes(workerId) ? ids.filter((x) => x !== workerId) : [...ids, workerId]));

  return (
    <div className="space-y-4" data-testid="payroll-workbench">
      {/* Summary + Push */}
      <div className={`grid grid-cols-2 gap-3 ${stats.hasRates ? (isPayrollMode ? 'md:grid-cols-6' : 'md:grid-cols-5') : (isPayrollMode ? 'md:grid-cols-5' : 'md:grid-cols-4')}`}>
        <Stat label="Workers" value={stats.workers} testId="pw-stat-workers" />
        <Stat label="Total Hours" value={stats.totalHours} testId="pw-stat-hours" />
        <Stat label="Overtime" value={stats.totalOT} accent="#FFA000" testId="pw-stat-overtime" />
        {stats.hasRates && (
          <Stat label="Est. Gross Pay" value={fmtDollar(stats.totalGross)} accent="#2DD4BF" testId="pw-stat-gross" />
        )}
        <Stat label="Anomalies" value={stats.totalAnomalies} accent={stats.totalAnomalies > 0 ? '#EF4444' : '#64748B'} testId="pw-stat-anomalies" />
        {isPayrollMode && (
          <div className="flex items-center justify-end col-span-2 md:col-span-1">
            <Button
              onClick={() => { setPushOpen(true); runPreview(); }}
              className="bg-[#1E88E5] hover:bg-blue-700 w-full"
              data-testid="pw-push-qbo-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Push to QuickBooks
            </Button>
          </div>
        )}
      </div>

      {!isPayrollMode && isAdmin && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded border border-[#1F2D3F] bg-[#0F1923] text-xs text-slate-400" data-testid="pw-review-mode-hint">
          <span>This report is for viewing and fixing time entries in a pay period. Locking, the pre-lock checklist and Push to QuickBooks live under Money → Payroll.</span>
          <Link to={`/payroll?tab=process&start=${startDate}&end=${endDate}`} className="text-[#1E88E5] hover:underline whitespace-nowrap" data-testid="pw-goto-process-link">
            Open Process &amp; Push →
          </Link>
        </div>
      )}

      {/* Averaging audit + OT alert controls (Aug 2026) */}
      {((missingAgreements?.count || 0) > 0 || isAdmin) && (
        <div className="flex flex-wrap items-center gap-2">
          {(missingAgreements?.count || 0) > 0 && (
            <button
              onClick={() => setMissingOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
              data-testid="pw-missing-agreements-chip"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {missingAgreements.count} averaging worker{missingAgreements.count === 1 ? '' : 's'} missing a signed agreement date
            </button>
          )}
          {isAdmin && isPayrollMode && otAlertCfg && (
            <button
              onClick={() => setOtAlertOpen(true)}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                otAlertCfg.enabled
                  ? 'border-teal-500/40 bg-teal-500/10 text-teal-300 hover:bg-teal-500/20'
                  : 'border-[#2D3748] bg-transparent text-slate-400 hover:text-slate-200'
              }`}
              data-testid="pw-ot-alert-settings-btn"
            >
              <Clock className="w-3.5 h-3.5" />
              OT approach alerts: {otAlertCfg.enabled ? `On (within ${otAlertCfg.hours_before}h of cap)` : 'Off'}
            </button>
          )}
        </div>
      )}

      {/* Missing agreement dates dialog */}
      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent className="max-w-lg" data-testid="pw-missing-agreements-dialog">
          <DialogHeader>
            <DialogTitle>Averaging workers missing a signed agreement date</DialogTitle>
            <DialogDescription>
              These staff are on the averaging overtime policy but have no signed agreement date on file.
              Add it in Staff → Edit (or select them all and use the bulk &quot;Set OT policy&quot; tool) to stay audit-ready.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#1F2D3F]">
            {(missingAgreements?.workers || []).map((w) => (
              <div key={w.id} className="py-2 flex items-center justify-between gap-3" data-testid={`pw-missing-agreement-row-${w.id}`}>
                <div>
                  <p className="text-sm text-white font-medium">{w.name}</p>
                  <p className="text-xs text-slate-500">{w.email}</p>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-300 border border-teal-500/30 font-semibold">
                  AVG {w.overtime_weekly_threshold || 44}h/wk
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingOpen(false)} data-testid="pw-missing-agreements-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OT approach alert settings dialog */}
      <Dialog open={otAlertOpen} onOpenChange={(o) => { if (!otAlertSaving) setOtAlertOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="pw-ot-alert-dialog">
          <DialogHeader>
            <DialogTitle>OT approach alerts</DialogTitle>
            <DialogDescription>
              Email admins when an averaging-agreement worker clocks out within a set number of hours of their averaged period cap. One alert per worker per pay period.
            </DialogDescription>
          </DialogHeader>
          {otAlertCfg && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <Checkbox
                  checked={!!otAlertCfg.enabled}
                  onCheckedChange={(v) => setOtAlertCfg({ ...otAlertCfg, enabled: !!v })}
                  data-testid="pw-ot-alert-enabled"
                />
                Enable OT approach alert emails
              </label>
              <div>
                <Label className="text-xs">Pay period anchor (any past period START date)</Label>
                <Input
                  type="date"
                  value={otAlertCfg.anchor_date || ''}
                  onChange={(e) => setOtAlertCfg({ ...otAlertCfg, anchor_date: e.target.value })}
                  data-testid="pw-ot-alert-anchor"
                />
                <p className="text-[11px] text-slate-500 mt-1">e.g. the first day of your current or any previous pay period — periods repeat from this date.</p>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Period length (days)</Label>
                  <Input
                    type="number" min="7" max="31"
                    value={otAlertCfg.period_days}
                    onChange={(e) => setOtAlertCfg({ ...otAlertCfg, period_days: parseInt(e.target.value, 10) || 14 })}
                    data-testid="pw-ot-alert-period-days"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Alert within (hours of cap)</Label>
                  <Input
                    type="number" min="1" max="40" step="0.5"
                    value={otAlertCfg.hours_before}
                    onChange={(e) => setOtAlertCfg({ ...otAlertCfg, hours_before: parseFloat(e.target.value) || 8 })}
                    data-testid="pw-ot-alert-hours-before"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Example: bi-weekly (14 days) at 44h/wk = 88h cap. With &quot;8h&quot;, admins are emailed once a worker passes 80h.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOtAlertOpen(false)} disabled={otAlertSaving} data-testid="pw-ot-alert-cancel">Cancel</Button>
            <Button
              onClick={saveOtAlertCfg}
              disabled={otAlertSaving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="pw-ot-alert-save"
            >
              {otAlertSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll lock banner / control (Jun 2026) */}
      {locked ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 p-3 rounded border border-amber-600/50 bg-amber-500/10"
          data-testid="pw-lock-banner"
        >
          <div className="flex items-center gap-2 text-sm text-amber-200">
            <Lock className="w-4 h-4 text-amber-400 shrink-0" />
            <span>
              Pay period <b>{activeLock.start_date} → {activeLock.end_date}</b> is locked
              {activeLock.locked_by_name ? <> by <b>{activeLock.locked_by_name}</b></> : null}
              {activeLock.locked_at ? ` on ${new Date(activeLock.locked_at).toLocaleDateString()}` : ''} — time entries are read-only.
            </span>
          </div>
          {isAdmin && isPayrollMode && (
            <Button size="sm" variant="outline" onClick={unlockPeriod} disabled={lockBusy} data-testid="pw-unlock-btn">
              <Unlock className="w-3 h-3 mr-1" /> Unlock
            </Button>
          )}
        </div>
      ) : (
        isAdmin && (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={downloadEditsCsv}
              className="text-teal-300 border-teal-700/60 hover:bg-teal-500/10 hover:text-teal-200"
              data-testid="pw-edits-csv-btn"
              title="CSV of every documented time edit this period — who, when, original → new times and the reason"
            >
              <FileText className="w-3 h-3 mr-1" />
              Adjustments CSV
            </Button>
            {isPayrollMode && (<>
            <Button
              size="sm"
              variant="outline"
              onClick={runChecklist}
              disabled={checklistBusy}
              className="text-blue-300 border-blue-700/60 hover:bg-blue-500/10 hover:text-blue-200"
              data-testid="pw-prelock-check-btn"
            >
              {checklistBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckSquare className="w-3 h-3 mr-1" />}
              Pre-lock checklist
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={lockPeriod}
              disabled={lockBusy}
              className="text-amber-300 border-amber-700/60 hover:bg-amber-500/10 hover:text-amber-200"
              data-testid="pw-lock-btn"
            >
              <Lock className="w-3 h-3 mr-1" /> Lock this period
            </Button>
            </>)}
          </div>
        )
      )}

      {/* Pre-payroll checklist dialog (Jun 2026 — Phase 5) */}
      <Dialog open={checklistOpen} onOpenChange={setChecklistOpen}>
        <DialogContent className="max-w-lg" data-testid="pw-prelock-dialog">
          <DialogHeader>
            <DialogTitle>Pre-payroll checklist — {startDate} → {endDate}</DialogTitle>
            <DialogDescription>
              {checklist?.total_entries ?? 0} time entries in this period.
              {checklist?.all_clear ? ' All clear — safe to lock.' : ' Review the flags below before locking.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {(checklist?.checks || []).map((c) => (
              <div key={c.key} className="p-3 rounded-lg border"
                style={{ borderColor: c.status === 'warn' ? 'rgba(255,160,0,0.5)' : '#2D3748',
                         background: c.status === 'warn' ? 'rgba(255,160,0,0.06)' : 'transparent' }}
                data-testid={`pw-check-${c.key}`}>
                <p className="text-sm font-medium flex items-center gap-2">
                  {c.status === 'warn'
                    ? <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                    : c.status === 'info'
                      ? <Info className="w-4 h-4 text-blue-400 shrink-0" />
                      : <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                  <span>{c.label}</span>
                  <span className="ml-auto font-bold" style={{ color: c.status === 'warn' ? '#FFA000' : c.status === 'info' ? '#1E88E5' : '#00C896' }}>
                    {c.count}
                  </span>
                </p>
                {c.details?.length > 0 && (
                  <ul className="mt-1.5 pl-6 text-xs space-y-0.5" style={{ color: '#A0AEC0' }}>
                    {c.details.map((d, i) => <li key={i}>• {d}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChecklistOpen(false)} data-testid="pw-prelock-close">Close</Button>
            {!locked && (
              <Button
                onClick={() => { setChecklistOpen(false); lockPeriod(); }}
                disabled={lockBusy || checklist?.can_lock === false}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="pw-prelock-lock-btn"
              >
                <Lock className="w-3 h-3 mr-1" /> Lock this period
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Adjustments — paper-trail receipt of every edit in the
          pay period plus one-click revert. Auto-hidden when zero edits. */}
      <PayrollAdjustments
        token={token}
        startDate={startDate}
        endDate={endDate}
        onChange={onRefresh}
        refreshKey={`${data?.total ?? 0}:${patchTick}`}
      />

      {/* Bulk selection action bar (Feb 2026) — appears when 1+ workers picked. */}
      {selectedWorkerIds.length > 0 && !locked && (
        <div
          className="flex items-center justify-between gap-3 p-3 rounded border border-blue-700/40 bg-blue-500/10"
          data-testid="pw-bulk-action-bar"
        >
          <div className="flex items-center gap-3 text-sm text-blue-100">
            <CheckSquare className="w-4 h-4 text-blue-300" />
            <span>
              <b data-testid="pw-bulk-count">{selectedWorkerIds.length}</b> worker{selectedWorkerIds.length === 1 ? '' : 's'} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              data-testid="pw-bulk-clear"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={openBulk}
              className="bg-[#1E88E5] hover:bg-blue-700"
              data-testid="pw-bulk-open-btn"
            >
              <Wand2 className="w-3 h-3 mr-1" />
              Bulk normalize hours
            </Button>
          </div>
        </div>
      )}

      {/* Workers table */}
      {/* Search + paging (Sep 2026 go-live: keeps the table fast at 500+ staff) */}
      {(rows.length > 8 || paged) && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <input
            value={rowSearch}
            onChange={(e) => { setRowSearch(e.target.value); setRowPage(1); }}
            placeholder="Search workers…"
            className="text-xs rounded-md px-2 py-1.5 w-52"
            style={{ background: '#1A2433', color: 'white', border: '1px solid #2D3748' }}
            data-testid="pw-worker-search"
          />
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: '#A0AEC0' }}
            title="Workers with sessions that have no project or sit on General Work — attribution only, pay is unaffected">
            <input type="checkbox" checked={legacyOnly} onChange={(e) => { setLegacyOnly(e.target.checked); setRowPage(1); }} className="accent-emerald-500" data-testid="pw-legacy-only" />
            Legacy / no project only
            {legacyTotals.workers > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(16,185,129,0.15)', color: '#34D399' }} data-testid="pw-legacy-count">{legacyTotals.workers} · {legacyTotals.hours.toFixed(2)} h</span>}
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: '#A0AEC0' }}
            title="Hide staff flagged 'Not on payroll' (contractors, test accounts, paid elsewhere) — they are never pushed to QuickBooks">
            <input type="checkbox" checked={hideExempt} onChange={(e) => { setHideExempt(e.target.checked); setRowPage(1); }} className="accent-sky-500" data-testid="pw-hide-exempt" />
            Hide staff not on payroll
            {exemptCount > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(56,189,248,0.15)', color: '#7DD3FC' }} data-testid="pw-exempt-count">{exemptCount}</span>}
          </label>
          {rowPageCount > 1 && (
            <div className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
              <Button size="sm" variant="outline" disabled={safeRowPage <= 1} onClick={() => goPage(safeRowPage - 1)} data-testid="pw-page-prev">Prev</Button>
              <span data-testid="pw-page-info">Page {safeRowPage} / {rowPageCount}</span>
              <Button size="sm" variant="outline" disabled={safeRowPage >= rowPageCount} onClick={() => goPage(safeRowPage + 1)} data-testid="pw-page-next">Next</Button>
            </div>
          )}
          <span className="text-[11px]" style={{ color: '#64748B' }} data-testid="pw-row-count">
            {legacyOnly ? `${pagedRows.length} worker${pagedRows.length === 1 ? '' : 's'} with legacy / no-project hours${paged ? ' on this page' : ''}` : hideExempt && paged ? `${pagedRows.length} of ${rows.length} on this page on payroll` : paged ? `${data.total} worker${data.total === 1 ? '' : 's'}${rowSearch.trim() ? ' match' : ''} · showing ${rows.length} on this page` : `${filteredRows.length} of ${rows.length} workers`}
          </span>
          {serverPaging?.remember && (
            <button
              type="button"
              onClick={resetView}
              className="text-[11px] underline ml-auto"
              style={{ color: '#94A3B8' }}
              title="Forget the remembered page, search and expanded workers"
              data-testid="pw-reset-view"
            >
              Reset view
            </button>
          )}
        </div>
      )}
      <div className="border border-[#2D3748] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F1923]">
            <tr className="text-left" style={{ color: '#94A3B8' }}>
              <th className="px-3 py-2 w-8">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={toggleAll}
                  data-testid="pw-select-all"
                  title={paged ? 'Select all workers on this page' : 'Select all workers'}
                />
              </th>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2">Worker</th>
              <th className="px-3 py-2">Total</th>
              <th className="px-3 py-2">Regular</th>
              <th className="px-3 py-2">Overtime</th>
              <th className="px-3 py-2">Breaks</th>
              {stats.hasRates && (
                <>
                  <th className="px-3 py-2">Rate</th>
                  <th className="px-3 py-2">Reg. Pay</th>
                  <th className="px-3 py-2">OT Pay</th>
                  <th className="px-3 py-2">Gross Pay</th>
                </>
              )}
              <th className="px-3 py-2">Anomalies</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-6 text-slate-500">No payroll data in this date range.</td></tr>
            ) : pagedRows.map((w) => (
              <React.Fragment key={w.worker_id}>
                <tr
                  className="border-t border-[#1F2D3F] hover:bg-[#0F1923] cursor-pointer"
                  onClick={() => toggle(w.worker_id)}
                  data-testid={`pw-worker-row-${w.worker_id}`}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={!!selectedWorkers[w.worker_id]}
                      onCheckedChange={() => toggleWorker(w.worker_id)}
                      data-testid={`pw-select-${w.worker_id}`}
                    />
                  </td>
                  <td className="px-3 py-3 cursor-pointer" data-testid={`pw-expand-${w.worker_id}`} aria-expanded={!!expanded[w.worker_id]}>
                    {expanded[w.worker_id]
                      ? <ChevronDown className="w-4 h-4 text-slate-400" />
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </td>
                  <td className="px-3 py-3 text-white cursor-pointer">
                    <div className="font-medium">
                      {w.worker_name}
                      {w.inactive && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300 border border-slate-500/40 align-middle font-semibold"
                          title="This worker is deactivated but has hours this period — confirm their final pay"
                          data-testid={`pw-inactive-badge-${w.worker_id}`}
                        >
                          DEACTIVATED
                        </span>
                      )}
                      {w.user_type === 'contractor' && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/40 align-middle font-semibold"
                          title="Contractor (Staff → Type)"
                          data-testid={`pw-contractor-badge-${w.worker_id}`}
                        >
                          CONTRACTOR
                        </span>
                      )}
                      {w.qb_payroll_exempt && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/40 align-middle font-semibold"
                          title="Not on payroll — hours stay here for reports/approvals but are never pushed to QuickBooks (Staff → Edit → Payroll status)"
                          data-testid={`pw-exempt-badge-${w.worker_id}`}
                        >
                          NOT ON PAYROLL
                        </span>
                      )}
                      {w.temp_hours > 0 && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 align-middle font-semibold"
                          title={`${w.temp_hours}h worked as a temp/mutual with another team this period`}
                          data-testid={`pw-temp-badge-${w.worker_id}`}
                        >
                          TEMP {w.temp_hours}h
                        </span>
                      )}
                      {w.overtime_policy === 'averaging' && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-300 border border-teal-500/30 align-middle font-semibold"
                          title={`Averaging agreement — overtime only past ${w.ot_threshold || 44}h/week averaged over the pay period${w.averaging_agreement_date ? ` (signed ${w.averaging_agreement_date})` : ''}`}
                          data-testid={`pw-avg-badge-${w.worker_id}`}
                        >
                          AVG
                        </span>
                      )}
                      {w.break_policy === 'paid' && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30 align-middle font-semibold"
                          title={`Paid breaks (${w.break_policy_source === 'worker' ? 'staff override' : 'company default'}) — no meal-break deduction or clock-out prompt`}
                          data-testid={`pw-break-paid-badge-${w.worker_id}`}
                        >
                          PAID BREAKS
                        </span>
                      )}
                      {w.overtime_policy === 'averaging' && !w.averaging_agreement_date && (
                        <span
                          className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 align-middle font-semibold"
                          title="No signed averaging agreement date on file — add it in Staff → Edit to keep records audit-ready"
                          data-testid={`pw-avg-missing-date-${w.worker_id}`}
                        >
                          ⚠ no signed date
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{w.email}</div>
                    {w.overtime_policy === 'averaging' && (() => {
                      const days = Math.round((new Date(`${endDate}T00:00:00Z`) - new Date(`${startDate}T00:00:00Z`)) / 86400000) + 1;
                      const cap = Math.round((w.ot_threshold || 44) * (days / 7) * 100) / 100;
                      const used = w.total_hours || 0;
                      const pct = Math.min(100, (used / cap) * 100);
                      const remaining = Math.round((cap - used) * 100) / 100;
                      return (
                        <div className="mt-1.5 w-44" data-testid={`pw-ot-meter-${w.worker_id}`}>
                          <div className="h-1.5 rounded-full bg-[#1F2D3F] overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, background: used > cap ? '#FFA000' : '#2DD4BF' }}
                            />
                          </div>
                          <p className="text-[10px] mt-0.5" style={{ color: used > cap ? '#FFA000' : '#64748B' }}>
                            {used > cap
                              ? `${fmtHours(used - cap)} into OT (cap ${fmtHours(cap)})`
                              : `${fmtHours(remaining)} until OT (${fmtHours(used)} of ${fmtHours(cap)})`}
                          </p>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-3 text-white">
                    {fmtHours(w.total_hours)}
                    {w.payroll_hours != null && Math.abs((w.payroll_hours || 0) - (w.total_hours || 0)) > 0.005 && (
                      <div className="text-[10px] font-semibold" style={{ color: '#7BB8E8' }} title="Payroll hours including the callback minimum top-up — pay is computed from this" data-testid={`pw-payroll-hours-${w.worker_id}`}>
                        pays {fmtHours(w.payroll_hours)}
                      </div>
                    )}
                    {(w.trimmed_hours || 0) > 0.005 && (
                      <div className="text-[10px]" style={{ color: '#FFA000' }} title="Hours removed via documented time-entry edits (with reasons, audit-trailed) this period" data-testid={`pw-trimmed-hours-${w.worker_id}`}>
                        −{fmtHours(w.trimmed_hours)} by edits
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3" style={{ color: '#A0AEC0' }}>{fmtHours(w.regular_hours)}</td>
                  <td className="px-3 py-3" style={{ color: w.overtime_hours > 0 ? '#FFA000' : '#64748B' }}>{fmtHours(w.overtime_hours)}</td>
                  <td className="px-3 py-3" style={{ color: w.break_minutes > 0 ? '#2DD4BF' : '#64748B' }} data-testid={`pw-break-min-${w.worker_id}`}>
                    {w.break_minutes > 0 ? `${Math.round(w.break_minutes)}m` : '—'}
                  </td>
                  {stats.hasRates && (
                    <>
                      <td className="px-3 py-3" style={{ color: '#A0AEC0' }} data-testid={`pw-rate-${w.worker_id}`}>
                        {w.hourly_rate != null ? `$${w.hourly_rate.toFixed(2)}/h` : '—'}
                      </td>
                      <td className="px-3 py-3" style={{ color: '#A0AEC0' }} data-testid={`pw-reg-pay-${w.worker_id}`}>
                        {fmtDollar(w.regular_pay)}
                      </td>
                      <td className="px-3 py-3" style={{ color: w.ot_pay > 0 ? '#FFA000' : '#64748B' }} data-testid={`pw-ot-pay-${w.worker_id}`}>
                        {fmtDollar(w.ot_pay)}
                      </td>
                      <td className="px-3 py-3 font-semibold" style={{ color: w.gross_pay > 0 ? '#2DD4BF' : '#64748B' }} data-testid={`pw-gross-pay-${w.worker_id}`}>
                        {fmtDollar(w.gross_pay)}
                        {(w.callback_premium || 0) > 0 && (
                          <div className="text-[10px] font-normal" style={{ color: '#F472B6' }} title="Callback rate premium included in gross (callback hours × rate × (multiplier − 1))" data-testid={`pw-callback-premium-${w.worker_id}`}>
                            incl. +{fmtDollar(w.callback_premium)} callback
                          </div>
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-3">
                    {w.anomalies?.length > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-red-500/10 border border-transparent hover:border-red-700/40 transition-colors cursor-pointer"
                            style={{ color: '#EF4444' }}
                            data-testid={`pw-anomaly-badge-${w.worker_id}`}
                            title="Click for anomaly details"
                          >
                            <AlertTriangle className="w-3 h-3" />
                            <span className="text-xs font-semibold">{w.anomalies.length}</span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="end"
                          className="w-80 max-h-80 overflow-y-auto bg-[#0F1923] border-amber-700/40 text-amber-100"
                          data-testid={`pw-anomaly-popover-${w.worker_id}`}
                        >
                          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-amber-700/40">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            <div className="text-sm font-semibold">
                              {w.anomalies.length} anomal{w.anomalies.length === 1 ? 'y' : 'ies'} for {w.worker_name}
                            </div>
                          </div>
                          <ul className="space-y-1.5 text-xs">
                            {w.anomalies.map((a, i) => (
                              <li
                                key={`${w.worker_id}-anom-${i}`}
                                className="flex items-start gap-2"
                                data-testid={`pw-anomaly-item-${w.worker_id}-${i}`}
                              >
                                <span className="text-amber-400 mt-0.5">•</span>
                                <span className="text-amber-100/90">{a}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[10px] text-amber-200/60 mt-3 pt-2 border-t border-amber-700/40">
                            Expand the worker row to edit individual sessions.
                          </p>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                </tr>
                {expanded[w.worker_id] && (
                  <tr className="bg-[#0B1320]">
                    <td colSpan={8} className="px-4 py-4">
                      {/* Top toolbar — Add Session for missing days. */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-slate-400">
                          {w.sessions?.length || 0} session{w.sessions?.length === 1 ? '' : 's'} for {w.worker_name}
                        </div>
                        <div className="flex items-center gap-2">
                          {workerSelectedIds(w).length > 0 && !locked && (
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                              onClick={() => openSessFix(w)}
                              data-testid={`pw-fix-sessions-${w.worker_id}`}
                            >
                              <Wand2 className="w-3 h-3 mr-1" /> Fix {workerSelectedIds(w).length} selected
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => openAdd(w)}
                            disabled={locked}
                            data-testid={`pw-add-session-${w.worker_id}`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add session
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => { ensureProjects(); setWeekFor(w); }}
                            disabled={locked}
                            title="Add a whole week of sessions in one grid"
                            data-testid={`pw-add-week-${w.worker_id}`}
                          >
                            <CalendarDays className="w-3 h-3 mr-1" /> Add week
                          </Button>
                        </div>
                      </div>
                      {/* Anomaly summary banner */}
                      {w.anomalies?.length > 0 && (
                        <div className="mb-3 p-2 rounded border border-amber-700/40 bg-amber-500/5 text-xs text-amber-300 space-y-0.5">
                          {w.anomalies.map((a, i) => (
                            <div key={`${w.worker_id}-anom-${i}`} className="flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{a}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Sessions table */}
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left" style={{ color: '#64748B' }}>
                            <th className="px-2 py-1 w-6">
                              <Checkbox
                                checked={(() => {
                                  const ids = (w.sessions || []).filter((s) => s.entry_id).map((s) => s.entry_id);
                                  return ids.length > 0 && ids.every((id) => sessSel[id]);
                                })()}
                                onCheckedChange={() => toggleAllSessions(w)}
                                title="Select all sessions for batch fixing"
                                data-testid={`pw-sess-select-all-${w.worker_id}`}
                              />
                            </th>
                            <th className="px-2 py-1">Date</th>
                            <th className="px-2 py-1">Clock In</th>
                            <th className="px-2 py-1">Clock Out</th>
                            <th className="px-2 py-1">Hours</th>
                            <th className="px-2 py-1">Job</th>
                            <th className="px-2 py-1">Flags</th>
                            <th className="px-2 py-1 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {w.sessions?.map((s) => (
                            <tr key={s.entry_id || `${s.date}-${s.clock_in}`} className="border-t border-[#1F2D3F]">
                              <td className="px-2 py-2">
                                {s.entry_id ? (
                                  <Checkbox
                                    checked={!!sessSel[s.entry_id]}
                                    onCheckedChange={() => toggleSession(s.entry_id)}
                                    data-testid={`pw-sess-select-${s.entry_id}`}
                                  />
                                ) : null}
                              </td>
                              <td className="px-2 py-2 text-white">{s.date}</td>
                              <td className="px-2 py-2 text-white">{fmtTime(s.clock_in)}</td>
                              <td className="px-2 py-2 text-white">{s.clock_out ? fmtTime(s.clock_out) : <span className="text-amber-400">Open</span>}</td>
                              <td className="px-2 py-2 text-white">{fmtHours(s.hours)}</td>
                              <td className="px-2 py-2 text-slate-300">
                                {pwDrill && (s.job_id || s.project_id) ? (
                                  <button type="button" onClick={() => pwDrill(s.job_id ? { type: 'job', id: s.job_id, label: s.job_title } : { type: 'project', id: s.project_id, label: s.project_name })}
                                    className="text-sky-300 hover:underline underline-offset-2 text-left" title="Location, service, duration and related reports" data-testid={`pw-drill-${s.entry_id}`}>
                                    {s.job_title || s.project_name}
                                  </button>
                                ) : (s.job_title || s.project_name || <span className="text-slate-600">—</span>)}
                                {s.job_number && <div className="text-[10px] text-slate-500">{s.job_number}</div>}
                                {isLegacySession(s) && (
                                  <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(16,185,129,0.12)', color: '#34D399' }}
                                    title="No project (or General Work). Payroll is unaffected — re-allocate from Approvals if it belongs to a real project." data-testid={`pw-legacy-badge-${s.entry_id}`}>
                                    {s.project_name ? 'General Work' : 'No project'}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {s.auto_timeout && <Flag color="#EF4444" label="Auto-closed" testId={`pw-flag-auto-${s.entry_id}`} />}
                                  {s.edited && <Flag color="#1E88E5" label="Edited" testId={`pw-flag-edited-${s.entry_id}`} />}
                                  {s.is_temp_team_shift && <Flag color="#A78BFA" label="Temp" title={`Mutual/coverage shift punched in with ${s.temp_for_team_name || 'another team'} — worker is not on that team's roster`} testId={`pw-flag-temp-${s.entry_id}`} />}
                                  {s.callback_shift && <Flag color="#F472B6" label={`Call-out${s.callout_topup ? ` +${s.callout_topup}h top-up` : ''}`} title="After-hours call-out: paid at least the company minimum; top-up hours are regular pay" testId={`pw-flag-callback-${s.entry_id}`} />}
                                  {isAdmin && (
                                    <button
                                      type="button"
                                      onClick={() => toggleCallout(w.worker_id, s)}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold hover:opacity-80"
                                      style={{ border: '1px dashed rgba(244,114,182,0.5)', color: '#F472B6' }}
                                      title={s.callback_shift ? 'Remove the call-out tag (reason recorded)' : 'Tag this session as an after-hours call-out (reason recorded)'}
                                      data-testid={`pw-callout-toggle-${s.entry_id}`}
                                    >
                                      {s.callback_shift ? 'Untag call-out' : 'Tag call-out'}
                                    </button>
                                  )}
                                  {s.is_offline_sync && <Flag color="#FFA000" label="Offline sync" title="Clock punch was captured offline on the worker's device and synced later — double-check the times before approving" testId={`pw-flag-offline-${s.entry_id}`} />}
                                  {s.outside_project_dates && <Flag color="#FBBF24" label="Outside project dates" title="This session's date is outside the project's start/end dates (Projects → Edit). Hours still count — check the project assignment." testId={`pw-flag-outside-dates-${s.entry_id}`} />}
                                  {roundingFlag(s) != null && (
                                    <span
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                      style={{ background: 'rgba(255,160,0,0.12)', color: '#FFA000', border: '1px solid rgba(255,160,0,0.4)' }}
                                      title={`Within 15 min of ${roundingFlag(s)}h — near a whole hour, worth a review. If there's a documented reason (e.g. forgot to clock out), tick the box and use "Fix selected" to set it to exactly ${roundingFlag(s)}h with a reason on record.`}
                                      data-testid={`pw-flag-rounding-${s.entry_id}`}
                                    >
                                      ≈{roundingFlag(s)}h
                                    </span>
                                  )}
                                  {!s.clock_out && <Flag color="#FFA000" label="Open" testId={`pw-flag-open-${s.entry_id}`} />}
                                  {s.source === 'kiosk' && <Flag color="#2DD4BF" label="Kiosk" testId={`pw-flag-kiosk-${s.entry_id}`} />}
                                  {(s.punch_photo_id || s.punch_out_photo_id) && (
                                    <button
                                      type="button"
                                      onClick={() => openPunchPhotos(w, s)}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                      style={{ background: 'rgba(45,212,191,0.12)', color: '#2DD4BF', border: '1px solid rgba(45,212,191,0.4)' }}
                                      title="View punch photos (buddy-punch check)"
                                      data-testid={`pw-punch-photos-${s.entry_id}`}
                                    >
                                      <Camera className="w-3 h-3" /> Photo
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={() => openEdit(w, s)}
                                    disabled={!s.entry_id || locked}
                                    data-testid={`pw-edit-session-${s.entry_id}`}
                                  >
                                    <Edit3 className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                  {isAdmin && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                                      onClick={() => { setDeleteReason(''); setDeleting({ worker: w, session: s }); }}
                                      disabled={!s.entry_id || locked}
                                      title="Delete this time entry (e.g. clocked in on a day off)"
                                      data-testid={`pw-delete-session-${s.entry_id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Kiosk punch photo viewer (buddy-punch review) */}
      <KioskPhotoDialog photoView={photoView} onClose={() => setPhotoView(null)} photoData={photoData} />

      {/* Delete Session Dialog (admin only) */}
      <DeleteSessionDialog
        deleting={deleting}
        onClose={() => setDeleting(null)}
        busy={deletingBusy}
        reason={deleteReason}
        setReason={setDeleteReason}
        onConfirm={confirmDelete}
      />

      {/* Batch-fix selected sessions dialog (Jun 2026) */}
      <SessionFixDialog
        sessFix={sessFix}
        onClose={() => setSessFix(null)}
        form={sessForm}
        setForm={setSessForm}
        onSave={saveSessFix}
        saving={sessSaving}
      />

      {/* Edit Session Dialog */}
      <EditSessionDialog
        editing={editing}
        onClose={() => setEditing(null)}
        form={editForm}
        setForm={setEditForm}
        onSave={saveEdit}
        saving={savingEdit}
        projects={projects}
        projectsLoading={projectsLoading}
        projectsError={projectsError}
        onRetryProjects={loadProjects}
        recentProjectIds={recentProjectIds(editing?.worker)}
        jobsCatalog={jobsCatalog}
        jobsLoading={jobsLoading}
      />

      {/* Add Session Dialog */}
      <AddSessionDialog
        worker={addingForWorker}
        onClose={() => setAddingForWorker(null)}
        form={addForm}
        setForm={setAddForm}
        onSubmit={submitAdd}
        saving={savingAdd}
        projects={projects}
        projectsLoading={projectsLoading}
        projectsError={projectsError}
        onRetryProjects={loadProjects}
        recentProjectIds={recentProjectIds(addingForWorker)}
        jobsCatalog={jobsCatalog}
        jobsLoading={jobsLoading}
      />

      {/* Week Entry Dialog */}
      {weekFor && (
        <WeekEntryDialog
          worker={weekFor}
          onClose={() => setWeekFor(null)}
          token={token}
          startDate={startDate}
          crew={rows.map((r) => ({ worker_id: r.worker_id, worker_name: r.worker_name }))}
          onDone={async (res) => {
            toast({
              title: `${res.created_count} session${res.created_count === 1 ? '' : 's'} added (${res.total_hours}h)`,
              description: res.error_count ? `${res.error_count} day${res.error_count === 1 ? '' : 's'} could not be added — see the grid.` : `Week added for ${weekFor.worker_name}.`,
              variant: res.error_count ? 'destructive' : undefined,
            });
            pendingScrollWorkerRef.current = weekFor.worker_id;
            if (res.crew?.length) await onRefresh?.(); else await refreshWorker(weekFor.worker_id);
          }}
          projects={projects}
          projectsLoading={projectsLoading}
          projectsError={projectsError}
          onRetryProjects={loadProjects}
        />
      )}

      {/* Bulk Normalize Dialog */}
      <BulkNormalizeDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        form={bulkForm}
        updateForm={updateBulkForm}
        selectedCount={selectedWorkerIds.length}
        startDate={startDate}
        endDate={endDate}
        selectionDays={selectionDays}
        preview={bulkPreview}
        onSubmit={submitBulk}
        saving={bulkSaving}
      />

      {/* Push-to-QBO Dialog */}
      <PushToQboDialog
        open={pushOpen}
        toast={toast}
        onClose={closePushDialog}
        startDate={startDate}
        endDate={endDate}
        previewLoading={previewLoading}
        previewError={previewError}
        previewResult={previewResult}
        pushResult={pushResult}
        totalReady={totalReady}
        totalUnapproved={totalUnapproved}
        totalSkipped={totalSkipped}
        totalBlockers={totalBlockers}
        totalAlreadyPushed={totalAlreadyPushed}
        totalExempt={totalExempt}
        excludedIds={excludedIds}
        onToggleExcluded={toggleExcluded}
        onRefreshPreview={runPreview}
        onPush={runPush}
        pushing={pushing}
        pushProgress={pushProgress}
        token={token}
        onDownloadCsv={downloadPayrollCsv}
        csvDownloading={csvDownloading}
      />
    </div>
  );
};

export default PayrollWorkbench;
